import React, { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Phone, Mail, Linkedin, FileText, Calendar, TrendingUp, Pencil, Save, X } from 'lucide-react';
import { activitiesAPI, pipelineAPI, contactsAPI } from '@/lib/api';
import { useContactTypes } from '@/lib/useContactTypes';
import { useT } from '@/lib/LanguageContext';
import { safeFormat } from '@/lib/utils';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';

const SOURCES = [
  { id: 'website', label: 'Website' },
  { id: 'referral', label: 'Referral' },
  { id: 'manual', label: 'Manual / Cold Outreach' },
  { id: 'apollo', label: 'Imported' },
  { id: 'existing', label: 'Existing Customer' },
];

const KINDS = [
  { id: 'person', label: 'Person' },
  { id: 'company', label: 'Company' },
];

export function ContactDetailSheet({ contact, stages = [], isOpen, onClose, onUpdate, teamMap = {} }) {
  const t = useT();
  const { types: CONTACT_TYPES, colorFor, labelFor } = useContactTypes();
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [showActivityForm, setShowActivityForm] = useState(null);
  const activityForm = useForm();
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && contact) loadActivities();
    setEditMode(false);
  }, [isOpen, contact?._id]);

  const loadActivities = async () => {
    try {
      setLoadingActivities(true);
      const res = await activitiesAPI.getByContact(contact._id);
      setActivities(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleStageChange = async (newStage) => {
    try {
      await pipelineAPI.moveStage(contact._id, newStage);
      toast.success(t('pipeline.stage_updated'));
      onUpdate?.();
    } catch {
      toast.error('Failed to update stage');
    }
  };

  const handleEnterEdit = () => {
    setEditData({
      email: contact.email || '',
      phone: contact.phone || '',
      position: contact.position || '',
      linkedin_url: contact.linkedin_url || '',
      contact_type: contact.contact_type || '',
      source: contact.source || 'manual',
      kind: contact.kind || 'person',
      street: contact.street || '',
      city: contact.city || '',
      state: contact.state || '',
      postal_code: contact.postal_code || '',
      country: contact.country || '',
      notes: contact.notes || '',
    });
    setEditMode(true);
  };

  const handleSaveContact = async () => {
    setSaving(true);
    try {
      await contactsAPI.update(contact._id, editData);
      toast.success('Contact updated');
      setEditMode(false);
      onUpdate?.();
    } catch {
      toast.error('Failed to update contact');
    } finally {
      setSaving(false);
    }
  };

  const handleLogActivity = async (data) => {
    try {
      await activitiesAPI.create({
        contact_id: contact._id,
        project_id: contact.project_id || null,
        type: showActivityForm,
        subject: data.subject || showActivityForm,
        body: data.notes || '',
        direction: 'outbound',
      });
      toast.success(t('pipeline.activity'));
      activityForm.reset();
      setShowActivityForm(null);
      loadActivities();
    } catch {
      toast.error('Failed to log activity');
    }
  };

  const iconFor = (type) => {
    const map = { call: Phone, email: Mail, linkedin: Linkedin, meeting: Calendar, stage_change: TrendingUp };
    return map[type] || FileText;
  };

  if (!contact) return null;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <div className="flex items-start justify-between gap-2">
            <div>
              <SheetTitle className="text-xl font-bold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                {contact.full_name}
              </SheetTitle>
              <SheetDescription>{contact.company || t('common.no_company')}</SheetDescription>
              {contact.email_opt_out && (
                <Badge variant="outline" className="mt-2 text-xs font-normal text-amber-700 border-amber-300 bg-amber-50">
                  Unsubscribed from email
                </Badge>
              )}
            </div>
            {!editMode ? (
              <Button variant="outline" size="sm" onClick={handleEnterEdit} className="h-8 gap-1.5 text-xs shrink-0">
                <Pencil className="w-3.5 h-3.5" />Edit
              </Button>
            ) : (
              <div className="flex gap-1.5 shrink-0">
                <Button size="sm" disabled={saving} onClick={handleSaveContact} className="h-8 gap-1.5 text-xs" style={{ backgroundColor: '#A62B1F', color: 'white' }}>
                  <Save className="w-3.5 h-3.5" />Save
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setEditMode(false)} className="h-8 px-2">
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
          </div>
        </SheetHeader>

        <div className="mt-6 space-y-5">
          {editMode ? (
            <div className="space-y-3 p-3 border border-[#E3DDD1] rounded-lg bg-[#FBF8F2]">
              <p className="text-xs font-semibold text-[#5C6670] uppercase tracking-wide">Edit Contact</p>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={editData.email}
                  onChange={e => setEditData(f => ({ ...f, email: e.target.value }))}
                  placeholder="Email"
                  className="h-8 text-sm"
                />
                <Input
                  value={editData.phone}
                  onChange={e => setEditData(f => ({ ...f, phone: e.target.value }))}
                  placeholder="Phone"
                  className="h-8 text-sm"
                />
              </div>
              <Input
                value={editData.position}
                onChange={e => setEditData(f => ({ ...f, position: e.target.value }))}
                placeholder="Position"
                className="h-8 text-sm"
              />
              <Input
                value={editData.linkedin_url}
                onChange={e => setEditData(f => ({ ...f, linkedin_url: e.target.value }))}
                placeholder="LinkedIn URL"
                className="h-8 text-sm"
              />
              <div className="grid grid-cols-2 gap-2">
                <Select value={editData.kind} onValueChange={v => setEditData(f => ({ ...f, kind: v }))}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {KINDS.map(k => <SelectItem key={k.id} value={k.id}>{k.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={editData.source} onValueChange={v => setEditData(f => ({ ...f, source: v }))}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SOURCES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {CONTACT_TYPES?.length > 0 && (
                <Select value={editData.contact_type} onValueChange={v => setEditData(f => ({ ...f, contact_type: v }))}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>
                    {CONTACT_TYPES.map(ct => <SelectItem key={ct.id} value={ct.id}>{ct.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Input
                value={editData.street}
                onChange={e => setEditData(f => ({ ...f, street: e.target.value }))}
                placeholder="Street Address"
                className="h-8 text-sm"
              />
              <div className="grid grid-cols-2 gap-2">
                <Input value={editData.city} onChange={e => setEditData(f => ({ ...f, city: e.target.value }))} placeholder="City" className="h-8 text-sm" />
                <Input value={editData.state} onChange={e => setEditData(f => ({ ...f, state: e.target.value }))} placeholder="State / Province" className="h-8 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input value={editData.postal_code} onChange={e => setEditData(f => ({ ...f, postal_code: e.target.value }))} placeholder="Postal Code" className="h-8 text-sm" />
                <Input value={editData.country} onChange={e => setEditData(f => ({ ...f, country: e.target.value }))} placeholder="Country" className="h-8 text-sm" />
              </div>
              <Select value={editData.language || 'en'} onValueChange={v => setEditData(f => ({ ...f, language: v }))}>
                <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Language" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="es">Spanish</SelectItem>
                </SelectContent>
              </Select>
              <Textarea
                value={editData.notes}
                onChange={e => setEditData(f => ({ ...f, notes: e.target.value }))}
                placeholder="Notes..."
                rows={2}
                className="text-sm"
              />
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              {contact.email && (
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#5C6670]" />
                  <a href={`mailto:${contact.email}`} className="text-[#4C6B82] hover:underline">{contact.email}</a>
                </div>
              )}
              {contact.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#5C6670]" />
                  <a href={`tel:${contact.phone}`} className="text-[#4C6B82] hover:underline">{contact.phone}</a>
                </div>
              )}
              {contact.linkedin_url && (
                <div className="flex items-center gap-2">
                  <Linkedin className="w-4 h-4 text-[#5C6670]" />
                  <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[#4C6B82] hover:underline">LinkedIn</a>
                </div>
              )}
              {(contact.street || contact.city || contact.state || contact.postal_code || contact.country) && (
                <p className="text-[#5C6670]">
                  {[contact.street, contact.city, contact.state, contact.postal_code, contact.country].filter(Boolean).join(', ')}
                </p>
              )}
              {contact.notes && (
                <p className="text-[#1E2A33] whitespace-pre-wrap">{contact.notes}</p>
              )}
            </div>
          )}

          <Separator />

          {stages.length > 0 && (
            <div>
              <Label className="text-sm font-medium text-[#1E2A33]">{t('pipeline.stage')}</Label>
              <Select value={contact.stage} onValueChange={handleStageChange}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {stages.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-[#5C6670] mb-1">{t('common.type')}</p>
              <Badge variant="outline" className={colorFor(contact.contact_type)}>
                {labelFor(contact.contact_type) || contact.contact_type || 'Other'}
              </Badge>
              {contact.kind === 'company' && (
                <Badge variant="outline" className="ml-1 text-[#A62B1F] border-[#D99A90]">Company</Badge>
              )}
            </div>
            <div>
              <p className="text-xs text-[#5C6670] mb-1">{t('pipeline.assigned_to')}</p>
              <p className="text-[#1E2A33]">
                {contact.assigned_to
                  ? (teamMap[contact.assigned_to]?.full_name || teamMap[contact.assigned_to]?.email || contact.assigned_to.slice(0, 8) + '…')
                  : t('pipeline.unassigned')}
              </p>
            </div>
          </div>

          <Separator />

          <div>
            <p className="font-semibold text-[#1E2A33] text-sm mb-2" style={{ fontFamily: 'Manrope' }}>{t('pipeline.log_activity')}</p>
            <div className="grid grid-cols-2 gap-2">
              {[['call', Phone, t('pipeline.log_call')], ['email', Mail, t('pipeline.log_email')], ['linkedin', Linkedin, t('pipeline.log_linkedin')], ['note', FileText, t('pipeline.log_note')]].map(([type, Icon, label]) => (
                <Button key={type} variant="outline" size="sm" onClick={() => setShowActivityForm(type)} className="justify-start text-xs">
                  <Icon className="w-3 h-3 mr-1.5" />{label}
                </Button>
              ))}
            </div>
            {showActivityForm && (
              <form onSubmit={activityForm.handleSubmit(handleLogActivity)} className="mt-3 p-3 border rounded-lg space-y-2">
                <Input {...activityForm.register('subject')} placeholder={t('pipeline.subject_optional')} className="text-sm" />
                <Textarea {...activityForm.register('notes')} placeholder={t('pipeline.notes')} rows={2} className="text-sm" />
                <div className="flex gap-2">
                  <Button type="submit" size="sm" style={{ backgroundColor: '#A62B1F', color: 'white' }}>{t('pipeline.save')}</Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowActivityForm(null)}>{t('pipeline.cancel')}</Button>
                </div>
              </form>
            )}
          </div>

          <Separator />

          <div>
            <p className="font-semibold text-[#1E2A33] text-sm mb-3" style={{ fontFamily: 'Manrope' }}>{t('pipeline.activity')}</p>
            {loadingActivities ? (
              <p className="text-sm text-[#5C6670]">{t('common.loading')}</p>
            ) : activities.length === 0 ? (
              <p className="text-sm text-[#5C6670]">{t('pipeline.no_activities')}</p>
            ) : (
              <div className="space-y-2">
                {activities.map(a => {
                  const Icon = iconFor(a.type);
                  return (
                    <div key={a._id} className="flex gap-2 p-2 border rounded-lg">
                      <div className="flex items-center justify-center w-7 h-7 rounded-full bg-[#F3EFE7] flex-shrink-0">
                        <Icon className="w-3.5 h-3.5 text-[#5C6670]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-[#1E2A33]">{a.subject || a.type}</p>
                        {a.body && (
                          <div
                            className="tiptap-preview text-xs text-[#5C6670] mt-0.5 max-h-24 overflow-y-auto"
                            dangerouslySetInnerHTML={{ __html: a.body }}
                          />
                        )}
                        <p className="text-xs text-[#5C6670] mt-0.5">
                          {safeFormat(a.occurred_at, 'MMM d, yyyy')} · {a.performed_by}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
